import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, Button, Divider } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '../service';
import DismissAlertModal from './DismissAlertModal';
import ResolveAlertModal from './ResolveAlertModal';
import AlertDetailsModal from './AlertDetailsModal';

const getAlertTargetRoute = (alert) => {
    const text = `${alert.title || ''} ${alert.description || ''} ${alert.entityName || ''} ${alert.type || ''}`.toLowerCase();
    if (text.includes('room') || text.includes('hvac') || text.includes('clean') || text.includes('housekeeping')) {
        return { path: '/room-management', label: 'Rooms' };
    }
    if (text.includes('rate') || text.includes('ota') || text.includes('pricing') || text.includes('parity')) {
        return { path: '/pricing-management', label: 'Pricing' };
    }
    if (text.includes('reservation') || text.includes('booking') || text.includes('check-in') || text.includes('check-out')) {
        return { path: '/reservations', label: 'Reservations' };
    }
    if (text.includes('night audit') || text.includes('folio') || text.includes('ledger') || text.includes('finance')) {
        return { path: '/finance?tab=night-audit', label: 'Night Audit' };
    }
    if (text.includes('backup') || text.includes('database') || text.includes('security') || text.includes('system') || text.includes('audit')) {
        return { path: '/security', label: 'Security Logs' };
    }
    return null;
};

export default function AlertsPanel({ initialData }) {
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dismissModalOpen, setDismissModalOpen] = useState(false);
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedAlertId, setSelectedAlertId] = useState(null);
    const [selectedAlert, setSelectedAlert] = useState(null);

    const parseAlertItem = (a, idx) => {
        const targetRoute = a.targetRoute || (a.target ? { path: a.target, label: 'View Target' } : getAlertTargetRoute(a));
        return {
            id: a.id || a._id || `alert-${idx}`,
            title: a.title || a.name || a.type || 'System Alert',
            description: a.description || a.message || 'Operational notification requiring review.',
            timeAgo: a.timeAgo || (a.timestamp ? formatTimeAgo(a.timestamp) : (a.createdAt ? formatTimeAgo(a.createdAt) : 'Recent')),
            targetRoute
        };
    };

    const fetchAlerts = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const res = await DashboardService.fetchAlerts(tenantId, { status: 'Active' });
            const dataArray = Array.isArray(res) ? res : (res && Array.isArray(res.alerts) ? res.alerts : (res && Array.isArray(res.data) ? res.data : null));
            if (dataArray && dataArray.length > 0) {
                const mapped = dataArray.map(parseAlertItem);
                setAlerts(mapped);
            } else {
                setAlerts([]);
            }
        } catch (err) {
            console.error('Failed to fetch system alerts:', err);
            setAlerts([]);
        } finally {
            setLoading(false);
        }
    };

    const formatTimeAgo = (ts) => {
        try {
            const diff = Date.now() - new Date(ts).getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 60) return `${Math.max(1, mins)}m ago`;
            const hours = Math.floor(mins / 60);
            if (hours < 24) return `${hours}h ago`;
            return `${Math.floor(hours / 24)}d ago`;
        } catch {
            return 'Recent';
        }
    };

    useEffect(() => {
        if (initialData && Array.isArray(initialData) && initialData.length > 0) {
            const mapped = initialData.map(parseAlertItem);
            setAlerts(mapped);
        } else if (initialData && Array.isArray(initialData) && initialData.length === 0) {
            setAlerts([]);
        } else {
            fetchAlerts();
        }
    }, [initialData]);

    const handleDismissAll = async () => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            for (const a of alerts) {
                await DashboardService.updateAlertStatus(tenantId, a.id, 'Dismissed');
            }
        } catch (_) {}
        setAlerts([]);
    };

    const items = alerts;

    return (
        <>
            <Card sx={{ 
                borderRadius: 3, 
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
                border: '1px solid #f1f5f9',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            }}>
                <CardContent sx={{ p: 2.5, pb: 1.5, flexGrow: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.2 }}>
                                Alerts
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                {items.length} unresolved
                            </Typography>
                        </Box>
                        {items.length > 0 && (
                            <Typography 
                                variant="caption" 
                                onClick={handleDismissAll}
                                sx={{ 
                                    color: '#64748b', 
                                    fontSize: '0.75rem', 
                                    fontWeight: 500, 
                                    cursor: 'pointer',
                                    '&:hover': { color: '#0f172a' } 
                                }}
                            >
                                Dismiss All
                            </Typography>
                        )}
                    </Box>

                    {/* Alert Items or Empty State */}
                    {items.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {items.map((item, idx) => (
                                <Box 
                                    key={item.id || idx}
                                    sx={{ 
                                        p: 1.5, 
                                        borderRadius: '8px', 
                                        border: '1px solid #f1f5f9',
                                        bgcolor: '#ffffff'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', whiteSpace: 'nowrap', ml: 1 }}>
                                            {item.timeAgo}
                                        </Typography>
                                    </Box>

                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', display: 'block', mb: 1.25, lineHeight: 1.4 }}>
                                        {item.description}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography
                                            variant="caption"
                                            onClick={() => {
                                                setSelectedAlert(item);
                                                setDetailsModalOpen(true);
                                            }}
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '0.72rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                '&:hover': { color: '#0f172a' }
                                            }}
                                        >
                                            Details
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            py: 3.5, 
                            textAlign: 'center' 
                        }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                bgcolor: '#ecfdf5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1.5
                            }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 24, color: '#10b981' }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
                                All Clear
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, maxWidth: 220 }}>
                                No active alerts requiring immediate front desk attention.
                            </Typography>
                        </Box>
                    )}
                </CardContent>

                {/* Footer Link */}
                <Box>
                    <Divider sx={{ borderColor: '#f1f5f9' }} />
                    <Box 
                        onClick={() => navigate('/finance?tab=night-audit')}
                        sx={{ 
                            p: 1.5, 
                            px: 2.5,
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: '#64748b',
                            transition: 'all 0.2s',
                            '&:hover': { color: 'primary.main', bgcolor: 'rgba(99, 102, 241, 0.02)' }
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            Audit History
                        </Typography>
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                    </Box>
                </Box>
            </Card>

            <AlertDetailsModal
                open={detailsModalOpen}
                onClose={() => setDetailsModalOpen(false)}
                alert={selectedAlert}
                onResolve={(id) => {
                    setSelectedAlertId(id);
                    setResolveModalOpen(true);
                }}
                onDismiss={(id) => {
                    setSelectedAlertId(id);
                    setDismissModalOpen(true);
                }}
            />

            <DismissAlertModal
                open={dismissModalOpen}
                onClose={() => setDismissModalOpen(false)}
                alertId={selectedAlertId}
                onSuccess={fetchAlerts}
            />

            <ResolveAlertModal
                open={resolveModalOpen}
                onClose={() => setResolveModalOpen(false)}
                alertId={selectedAlertId}
                onSuccess={fetchAlerts}
            />
        </>
    );
}
